import { ChangeEvent, FormEvent, Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { FormattedMessage } from 'react-intl';
import { MinimalUser, ReduxState } from 'types';
import { formatMessage } from 'i18n/CurrentLocale';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFloppyDisk, faUserCircle } from '@fortawesome/free-solid-svg-icons';
import { companyUsersSliceActions } from 'features/users/companyUsersSlice';
import * as modalWindowActions from 'root/modal/modalWindowActions';
import * as validation from 'myParkdUtils/validationHandling';
import ErrorMessage from 'components/ErrorMessage';
import 'react-phone-number-input/style.css';
import PhoneInputWithCountrySelect, { isValidPhoneNumber } from 'react-phone-number-input';
import { ActionStatusDisplayer } from 'components/ActionStatusDisplayer';


type ValidationErrors = {
    givenName?: any,
    familyName?: any,
    email?: any,
    phoneNumber?: any
}

export const EditUserModal = (props: Props) => {

    const { userUpdateStatus } = props;
    const [user, setUser] = useState<MinimalUser>(props.modalData.user);

    useEffect(() => {
        props.clearUserUpdateStatus();
        setUser(props.modalData.user);
    }, []);

    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
        setUser({
            ...user,
            [e.target.name]: e.target.value
        });
    }

    const getValidationErrors = () => {
        let errors: ValidationErrors = {};
        errors.givenName = validation.checkIfNotNull(user.givenName);
        errors.familyName = validation.checkIfNotNull(user.familyName);
        errors.email = validation.checkIfValidEmailAddress(user.email);
        return errors;
    };

    const saveUser = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const validationSuccessful = validation.validate(getValidationErrors());
        if (validationSuccessful) {
            props.updateUser({ user });
        }
    }

    const isValidPhone = isValidPhoneNumber(user?.phoneNumber ?? '');
    const errors = getValidationErrors();

    const actionStatusHasChanged = !!(userUpdateStatus.loading || userUpdateStatus.succeeded || userUpdateStatus.error);

    return (
        <div className="panel radius-rectangle full-width-form confirmation-modal">
            <div className="panel-header">
                <h2 className="bold-title">
                    <FontAwesomeIcon icon={faUserCircle} />
                    <FormattedMessage id='MODAL_UPDATE_USER_TITLE' />
                </h2>
                <button data-testid="btn-close-modal" className="btn-close-modal" onClick={() => props.closeModal()} />
            </div>

            <div className="panel-content">
                {actionStatusHasChanged
                    ?
                    <>
                        <ActionStatusDisplayer
                            status={userUpdateStatus}
                            succeededMessage={formatMessage({ id: 'MODAL_EDIT_USER_ACTION_SUCCEEDED' })}
                            errorMessage={formatMessage({ id: 'MODAL_EDIT_USER_ACTION_ERROR' })}
                        />
                        <button className='btn-blue' onClick={() => props.closeModal()}>
                            <FormattedMessage id='GLOBAL_CLOSE' />
                        </button>
                    </>
                    :
                    <Fragment>
                        <form onSubmit={saveUser} className='align-left'>
                            <div className='input-group'>
                                <label htmlFor='inputGivenName'><FormattedMessage id='MODAL_EDIT_USER_LABEL_FIRST_NAME' /></label>
                                <input
                                    type='text'
                                    className='text-field'
                                    name='givenName'
                                    id='inputGivenName'
                                    value={user?.givenName}
                                    placeholder={formatMessage({ id: 'PLACEHOLDER_FIRST_NAME' })}
                                    onChange={(e) => onChange(e)}
                                />
                                <ErrorMessage msg={errors.givenName} />
                            </div>

                            <div className='input-group'>
                                <label htmlFor='inputFamilyName'><FormattedMessage id='MODAL_EDIT_USER_LABEL_LAST_NAME' /></label>
                                <input
                                    type='text'
                                    className='text-field'
                                    name='familyName'
                                    id='inputFamilyName'
                                    value={user?.familyName}
                                    placeholder={formatMessage({ id: 'PLACEHOLDER_LAST_NAME' })}
                                    onChange={(e) => onChange(e)}
                                />
                                <ErrorMessage msg={errors.familyName} />
                            </div>

                            <div className='input-group'>
                                <label htmlFor='inputEmail'><FormattedMessage id='MODAL_EDIT_USER_LABEL_EMAIL' /></label>
                                <input
                                    type='email'
                                    className='text-field'
                                    name='email'
                                    id='inputEmail'
                                    value={user?.email}
                                    placeholder={formatMessage({ id: 'PLACEHOLDER_EMAIL' })}
                                    onChange={(e) => onChange(e)}
                                />
                                <ErrorMessage msg={errors.email} />
                            </div>

                            <div className='input-group'>
                                <label htmlFor='inputPhone'><FormattedMessage id='MODAL_EDIT_USER_LABEL_PHONE' /></label>
                                <div className='phone-input-container'>
                                    <PhoneInputWithCountrySelect
                                        id="inputPhone"
                                        name="phonenumber"
                                        international
                                        defaultCountry='NL'
                                        placeholder={formatMessage({ id: 'PLACEHOLDER_PHONE' })}
                                        value={user.phoneNumber}
                                        onChange={(value) => setUser({ ...user, phoneNumber: value! })}
                                    />
                                </div>
                                {!isValidPhone && (
                                    <ErrorMessage msg={formatMessage({ id: 'VALIDATION_INVALID_PHONENUMBER' })} />
                                )}
                            </div>

                            <button type='submit' className='btn-blue'>
                                <FontAwesomeIcon icon={faFloppyDisk} size='1x' />&nbsp;
                                <FormattedMessage id="GLOBAL_SAVE" />
                            </button>

                        </form>
                    </Fragment>
                }

            </div>
        </div>
    );
};


type OwnProps = {
    modalData: {
        user: MinimalUser
    }
}
type StateProps = ReturnType<typeof mapStateToProps>
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps & OwnProps;

function mapStateToProps(state: ReduxState) {
    return {
        token: state.auth.token,
        userUpdateStatus: state.companyUsers.userUpdateStatus
    };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>) {
    return bindActionCreators({
        updateUser: companyUsersSliceActions.updateCompanyUser,
        clearUserUpdateStatus: companyUsersSliceActions.clearUserUpdateStatus,
        closeModal: modalWindowActions.closeModal,
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(EditUserModal);